/**
 * 任務積分分攤共用工具。
 *
 * 規則：
 *  - 任務本身仍以 totalScore / modifyScore / suppApprovedScore 等欄位儲存。
 *  - 多人任務時，「工作量類」積分（基本/調整/補單）依成員 weight 分攤；
 *    若 member 沒有 weight 欄位，預設 weight = 1（即平分）。
 *  - imgScore（圖片主管評分）與工時不分攤；它代表任務本身的品質/實際投入。
 *  - 顯示時請使用 round1() 將數字保留一位小數。
 *
 * 取代式補單（suppReplaceOriginal === true）：
 *  - 用於回報時技師在現場才發現實際做的事與原派工不同的情境。
 *  - 必須由主管核准 suppApprovedScore 後才生效；
 *    待審核期間 base / effective 仍照原本的 totalScore + modifyScore 計算，
 *    避免技師申請當下立刻被扣分。
 *  - 主管核准後，原任務的 totalScore + modifyScore 不再計入；
 *    生效積分等於核准的補單積分（仍依成員 weight 分攤）。
 *  - imgScore 不受影響，照常統計品質評分。
 */

export const round1 = (n) => Math.round(Number(n || 0) * 10) / 10;

export const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

/**
 * 取得單一成員的 weight，預設為 1。
 * 未來若 UI 加上「主責 1.5 / 協助 0.5」之類的設定，
 * 只需要在 member 物件寫入 weight 即可，這個 helper 會自動處理。
 */
export function getMemberWeight(member) {
  if (!member) return 1;
  const w = Number(member.weight);
  return Number.isFinite(w) && w > 0 ? w : 1;
}

/**
 * 任務的總 weight；若 member 物件不存在則退回 memberList 長度。
 * 用於做為分攤分母（每個 weight 單位的積分 = 任務積分 / 總weight）。
 */
export function getTotalWeight(task) {
  const list = Array.isArray(task && task.member) ? task.member : [];
  if (list.length > 0) {
    const sum = list.reduce((acc, m) => acc + getMemberWeight(m), 0);
    return sum > 0 ? sum : list.length;
  }
  const ml = Array.isArray(task && task.memberList) ? task.memberList : [];
  return ml.length > 0 ? ml.length : 1;
}

/**
 * 任務的成員人數（顯示「與 N 人共享」用）。
 */
export function getMemberCount(task) {
  if (Array.isArray(task && task.member) && task.member.length > 0) {
    return task.member.length;
  }
  if (Array.isArray(task && task.memberList) && task.memberList.length > 0) {
    return task.memberList.length;
  }
  return 1;
}

/**
 * 計算任務一個 weight 單位可分配到的積分（未四捨五入）。
 * 公式：每單位 = 任務積分 / 總weight
 */
export function calcUnitShare(task) {
  const totalWeight = getTotalWeight(task);
  const total = Number((task && task.totalScore) || 0);
  const modify = Number((task && task.modifyScore) || 0);
  const suppApproved = task && task.suppApprovedScore != null
    ? Number(task.suppApprovedScore)
    : null;
  const suppPending = Array.isArray(task && task.supplementaryTasks)
    ? task.supplementaryTasks.reduce(
      (s, t) => s + Number((t && t.score) || 0) * (Number(t && t.qty) || 1),
      0,
    )
    : 0;

  // 取代式補單：旗標為 true 且「主管已核准 suppApprovedScore」才生效；
  // 待審核期間維持原本的 base + modify，避免回報當下立刻被扣分。
  const isReplaceActive = task && task.suppReplaceOriginal === true && suppApproved != null;
  const baseAmount = isReplaceActive ? 0 : (total + modify);
  const modifyAmount = isReplaceActive ? 0 : modify;

  return {
    totalWeight,
    memberCount: getMemberCount(task),
    base: baseAmount / totalWeight,
    modify: modifyAmount / totalWeight,
    suppApproved: suppApproved == null ? null : suppApproved / totalWeight,
    suppPending: suppPending / totalWeight,
    effective: (baseAmount + (suppApproved || 0)) / totalWeight,
    imgScore: Number((task && task.imgScore) || 0),
    isReplaceActive,
  };
}

/**
 * 計算指定成員的積分份額（未四捨五入，方便累加）。
 * - member 可為 task.member 中對應的物件（保留 weight）；
 * - 若傳入 null/undefined，視為 weight = 1（平分）。
 */
export function calcMemberShare(task, member) {
  const unit = calcUnitShare(task);
  const w = getMemberWeight(member);
  return {
    memberCount: unit.memberCount,
    totalWeight: unit.totalWeight,
    weight: w,
    base: unit.base * w,
    modify: unit.modify * w,
    suppApproved: unit.suppApproved == null ? null : unit.suppApproved * w,
    suppPending: unit.suppPending * w,
    effective: unit.effective * w,
    imgScore: unit.imgScore,
    isReplaceActive: unit.isReplaceActive,
  };
}

/**
 * 依姓名取得該成員在任務中的份額。
 * 若 task.member 找不到對應物件（舊資料只有 memberList），會 fallback 為平分。
 */
export function calcShareForName(task, name) {
  const list = Array.isArray(task && task.member) ? task.member : [];
  const found = list.find((m) => m && m.name === name);
  return calcMemberShare(task, found || null);
}

/**
 * 將一批任務聚合到「人員」維度，輸出每位成員在這批任務中的累計指標。
 *
 * 重要規則：
 *  - 工作量類積分（base/modify/suppApproved/suppPending/effective）
 *    依 weight 分攤累加（多人任務時，每位只拿到自己 weight 比例的份額）。
 *  - 主管評分（imgScore）只統計「已評分的任務」（imgReviewed === true）：
 *      - imgScoreSum：已評分任務的 imgScore 總和（不分攤，每位成員拿全額）
 *      - imgReviewedCount：該成員「參與且已評分」的任務數
 *      - imgScoreAverage = imgScoreSum / imgReviewedCount
 *    這樣可以避免「未評分任務拉低平均」的失真問題。
 *  - taskSum 為該成員「所有」參與任務數（含未評分）。
 *  - regions 收集該成員出現過的區域集合。
 *
 * @param {Array} tasks 任務（events）陣列
 * @returns {Array} 聚合後成員陣列（順序與輸入無關）
 */
export function aggregateMembersFromTasks(tasks) {
  const map = new Map();
  const list = Array.isArray(tasks) ? tasks : [];
  list.forEach((task) => {
    if (!task) return;
    const unit = calcUnitShare(task);
    const region = task.region || '';
    const isImgReviewed = task.imgReviewed === true;
    const imgScoreVal = Number(task.imgScore || 0);
    const members = Array.isArray(task.member) ? task.member : [];
    members.forEach((m) => {
      if (!m || !m.name) return;
      const w = getMemberWeight(m);
      let agg = map.get(m.name);
      if (!agg) {
        agg = {
          name: m.name,
          regions: new Set(),
          taskSum: 0,
          base: 0,
          modify: 0,
          suppApproved: 0,
          suppPending: 0,
          effective: 0,
          imgScoreSum: 0,
          imgReviewedCount: 0,
        };
        map.set(m.name, agg);
      }
      if (region) agg.regions.add(region);
      agg.taskSum += 1;
      agg.base += unit.base * w;
      agg.modify += unit.modify * w;
      agg.suppApproved += (unit.suppApproved == null ? 0 : unit.suppApproved) * w;
      agg.suppPending += unit.suppPending * w;
      agg.effective += unit.effective * w;
      if (isImgReviewed) {
        agg.imgScoreSum += imgScoreVal;
        agg.imgReviewedCount += 1;
      }
    });
  });
  return Array.from(map.values()).map((a) => {
    const regionList = Array.from(a.regions).sort();
    const imgScoreAverage = a.imgReviewedCount > 0
      ? a.imgScoreSum / a.imgReviewedCount
      : null;
    return {
      name: a.name,
      regions: regionList,
      region: regionList.join('、'),
      taskSum: a.taskSum,
      base: a.base,
      modify: a.modify,
      suppApproved: a.suppApproved,
      suppPending: a.suppPending,
      effective: a.effective,
      imgScoreSum: a.imgScoreSum,
      imgReviewedCount: a.imgReviewedCount,
      imgScoreAverage,
    };
  });
}

function normalizeRegionFilter(regions) {
  if (regions == null) return null;
  if (Array.isArray(regions)) {
    const trimmed = regions.map((r) => String(r || '').trim()).filter((r) => r.length > 0);
    if (trimmed.length === 0) return null;
    return new Set(trimmed);
  }
  const single = String(regions || '').trim();
  if (!single) return null;
  return new Set([single]);
}

/**
 * 依生效積分（effective）取出 TOP N 成員。
 * 同分時以任務數降序排，皆相同時以姓名升序穩定排序。
 *
 * @param {Array} tasks 任務陣列
 * @param {Object} [options]
 * @param {number} [options.limit=10] 取前幾名
 * @param {string|string[]|null} [options.regions=null] 區域過濾（不傳=全部；傳字串=單區；傳陣列=多區）
 * @returns {Array} 前 N 名（已 round1）
 */
export function getTopMembersByEffective(tasks, options = {}) {
  const limit = Number.isFinite(options.limit) && options.limit > 0
    ? Math.floor(options.limit)
    : 10;
  const regionFilter = normalizeRegionFilter(options.regions);
  let filtered = Array.isArray(tasks) ? tasks : [];
  if (regionFilter) {
    filtered = filtered.filter((t) => t && regionFilter.has(t.region));
  }
  return aggregateMembersFromTasks(filtered)
    .filter((m) => m.effective > 0)
    .sort((a, b) => {
      if (b.effective !== a.effective) return b.effective - a.effective;
      if (b.taskSum !== a.taskSum) return b.taskSum - a.taskSum;
      return String(a.name).localeCompare(String(b.name), 'zh-Hant');
    })
    .slice(0, limit)
    .map((m, idx) => ({
      ...m,
      rank: idx + 1,
      base: round1(m.base),
      modify: round1(m.modify),
      suppApproved: round1(m.suppApproved),
      suppPending: round1(m.suppPending),
      effective: round1(m.effective),
    }));
}

/**
 * 依主管評分（imgScore）平均取出 TOP N 成員。
 * 平均的分母是「已評分任務數（imgReviewed === true）」，避免未評分任務拉低平均。
 * 同分時以已評分任務數降序排，皆相同時以姓名升序穩定排序。
 *
 * @param {Array} tasks 任務陣列
 * @param {Object} [options]
 * @param {number} [options.limit=10]
 * @param {number} [options.minReviewedCount=0] 最少已評分任務數門檻；0 = 不設
 * @param {string|string[]|null} [options.regions=null] 區域過濾
 * @returns {Array} 前 N 名（imgScoreAverage 保留 1 位小數）
 */
export function getTopMembersByImgScore(tasks, options = {}) {
  const limit = Number.isFinite(options.limit) && options.limit > 0
    ? Math.floor(options.limit)
    : 10;
  const minReviewed = Math.max(0, Number(options.minReviewedCount) || 0);
  const regionFilter = normalizeRegionFilter(options.regions);
  let filtered = Array.isArray(tasks) ? tasks : [];
  if (regionFilter) {
    filtered = filtered.filter((t) => t && regionFilter.has(t.region));
  }
  return aggregateMembersFromTasks(filtered)
    .filter((m) => m.imgReviewedCount > 0 && m.imgReviewedCount >= minReviewed)
    .sort((a, b) => {
      if (b.imgScoreAverage !== a.imgScoreAverage) {
        return b.imgScoreAverage - a.imgScoreAverage;
      }
      if (b.imgReviewedCount !== a.imgReviewedCount) {
        return b.imgReviewedCount - a.imgReviewedCount;
      }
      return String(a.name).localeCompare(String(b.name), 'zh-Hant');
    })
    .slice(0, limit)
    .map((m, idx) => ({
      ...m,
      rank: idx + 1,
      imgScoreAverage: round1(m.imgScoreAverage),
    }));
}
